import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { stringify } from "query-string";
import { matchesBoolean, matchesString } from "LIB/filters";
import { SUCCESS } from "LIB/submission";
import { Button, Search } from "MODULES/shared/components";
import {
  FilterSelect,
  Pagination,
  SearchFilter,
  TitleWithFilters
} from "MODULES/shared/components/Pagination";
import useCurrentUser from "APP_ROOT/hooks/useCurrentUser";
import { BUYING_STORE_ID, IRE } from "MODULES/PurchaseOrders/constants";
import {
  CORE_REPAIR_INVOICES_PAID,
  PURCHASE_FOR_EXCHANGE
} from "MODULES/RepairOrders/constants";
import { CLEAR_FILTERS_TEXT, SHIPPED, API_BASE } from "APP_ROOT/constants";
import { renderIf } from "MODULES/shared/decorators";
import { ADMIN, EXPORT_LIMIT } from "MODULES/users/constants";
import DateRangePicker from "MODULES/shared/components/DateRangePicker/DateRangePicker";
import Sum from "MODULES/PurchaseOrders/components/Search/Sum";
import api from "APP_ROOT/api";
import { toastr } from "react-redux-toastr";
import Table from "./Table";
import { purchaseOrdersPagination } from "../../pagination";

const defaultFilters = {
  buying_store: undefined,
  buying_store_id: undefined,
  cancelled: undefined,
  consignment: undefined,
  core_repair_invoices_paid: undefined,
  current_user: undefined,
  invoiced: undefined,
  not_paid: undefined,
  over_buying_limit: undefined,
  partially_received: undefined,
  purchase_for_exchange: undefined,
  purchase_for_sale: undefined,
  purchase_for_stock: undefined,
  purchase_for_repair: undefined,
  purchase_for_repair_assist: undefined,
  received: undefined,
  shipped: undefined,
  trace_hold: undefined,
  vendor_rma: undefined
};

const filters = [
  {
    label: "Received and Invoiced",
    filters: {
      ...defaultFilters,
      received: true,
      invoiced: true
    }
  },
  {
    label: "Received and Not Invoiced",
    filters: {
      ...defaultFilters,
      received: true,
      invoiced: false
    }
  },
  {
    label: "---"
  },
  {
    label: "My POs",
    filters: { current_user: true }
  },
  {
    label: "Received",
    filters: {
      received: true
    }
  },
  {
    label: "Partially Received",
    filters: { partially_received: true }
  },
  {
    label: "Not Received",
    filters: {
      received: false
    }
  },
  {
    label: "Cancelled",
    filters: {
      cancelled: true
    }
  },
  {
    label: "Not Cancelled",
    filters: {
      cancelled: false
    }
  },
  {
    label: "Invoiced",
    filters: {
      invoiced: true
    }
  },
  {
    label: "Not Invoiced",
    filters: {
      invoiced: false
    }
  },
  {
    label: "Not Paid / Remaining Balance",
    filters: { not_paid: true }
  },
  {
    label: "Open POs Awaiting Trace Pre-Approval",
    filters: {
      ...defaultFilters,
      trace_hold: true,
      received: false,
      cancelled: false
    }
  },
  {
    label: "Open POs Awaiting Purchaser Limit Approval",
    filters: {
      ...defaultFilters,
      over_buying_limit: true,
      received: false,
      cancelled: false
    }
  },
  {
    label: "Exchanges",
    filters: {
      purchase_for_exchange: true
    }
  },
  {
    label: "Purchase For Sale",
    filters: {
      purchase_for_sale: true
    }
  },
  {
    label: "Purchase For Stock",
    filters: {
      purchase_for_stock: true
    }
  },
  {
    label: "Purchase For Repair",
    filters: {
      purchase_for_repair: true
    }
  },
  {
    label: "Purchase For Repair Assist",
    filters: {
      purchase_for_repair_assist: true
    }
  },
  {
    label: "Cores Returned",
    filters: {
      purchase_for_exchange: true,
      shipped: true
    }
  },
  {
    label: "Cores Not Returned",
    filters: {
      purchase_for_exchange: true,
      shipped: false
    }
  },
  {
    label: "Core Repair Invoices Not Paid",
    filters: {
      core_repair_invoices_paid: false,
      purchase_for_exchange: true
    }
  },
  {
    label: "Vendor RMA",
    filters: {
      vendor_rma: true
    }
  },
  {
    label: "No Vendor RMA",
    filters: {
      vendor_rma: false
    }
  },
  {
    label: "Consignment",
    filters: {
      consignment: true
    }
  },
  {
    label: "Synced with Accounting",
    filters: {
      accounting_synced: true
    }
  },
  {
    label: "Not Synced with Accounting",
    filters: {
      accounting_synced: false
    }
  }
];

const filterTags = [
  {
    label: "Synced with Accounting",
    key: "accounting_synced",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Synced w/ Accounting",
    key: "accounting_synced",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Not Paid / Remaining Balance",
    key: "not_paid",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "My POs",
    key: "current_user"
  },
  {
    label: "Received",
    key: "received",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Received",
    key: "received",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Invoiced",
    key: "invoiced",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Invoiced",
    key: "invoiced",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Cancelled",
    key: "cancelled",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Cancelled",
    key: "cancelled",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Owned by IRE",
    key: "buying_store",
    matchesTarget: matchesString(IRE.toLowerCase())
  },
  {
    label: "Partially Received",
    key: "partially_received",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Purchase For Sale",
    key: "purchase_for_sale"
  },
  {
    label: "Purchase For Stock",
    key: "purchase_for_stock"
  },
  {
    label: "Purchase For Repair",
    key: "purchase_for_repair"
  },
  {
    label: "Purchase For Repair Assist",
    key: "purchase_for_repair_assist"
  },
  {
    label: "Over Buying Limit",
    key: "over_buying_limit",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Trace Hold",
    key: "trace_hold",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Exchanges",
    key: PURCHASE_FOR_EXCHANGE,
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Cores Returned",
    key: SHIPPED,
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Cores Not Returned",
    key: SHIPPED,
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Core Repair Invoices Not Paid",
    key: CORE_REPAIR_INVOICES_PAID,
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Vendor RMA",
    key: "vendor_rma",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "No Vendor RMA",
    key: "vendor_rma",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Consignment",
    key: "consignment",
    matchesTarget: matchesBoolean(true)
  }
];

const ExportButton = ({ searchProps }) => {
  const params = searchProps.paginator.get("filters").toJS();
  const purchaseOrderLineItemTotalCount = searchProps.paginator.get(
    "totalCount"
  );

  params.total_count = purchaseOrderLineItemTotalCount;
  return (
    <Button
      onClick={() => {
        if (purchaseOrderLineItemTotalCount > EXPORT_LIMIT) {
          toastr.info("Processing Report.");
          api.purchaseOrders.lineItems.export(params).then(() => SUCCESS);
        } else {
          window.open(
            `${API_BASE}/purchase_order_line_items/export?${stringify(params)}`,
            "_blank"
          );
          toastr.success(
            "Processing Purchase Order Report. Please wait for the PDF to generate."
          );
        }
      }}
    >
      Export
    </Button>
  );
};

const meetsExportCriteria = renderIf(
  ({ searchProps, currentUser }) =>
    searchProps.paginator.get("totalCount") <= 450 || currentUser.role === ADMIN
);

const DecoratedExportButton = meetsExportCriteria(ExportButton);

function PurchaseOrdersSearch({ pushHistory }) {
  const currentUser = useCurrentUser();

  const filterTagsWithLocations = currentUser.locations.map(l => ({
    label: `Owned by ${l.name}`,
    key: BUYING_STORE_ID,
    matchesTarget: matchesString(l.id)
  }));

  const filtersWithLocations = currentUser.locations.map((l, i) => [
    {
      label: `All Owned By ${l.name}`,
      filters: { buying_store_id: l.id }
    },
    {
      label: `Open POs owned by ${l.name}`,
      filters: {
        ...defaultFilters,
        received: false,
        cancelled: false,
        buying_store_id: l.id
      },
      hr: currentUser.locations.length - 1 === i
    }
  ]);

  return (
    <Search search={purchaseOrdersPagination} syncWithUrl>
      {searchProps => (
        <div>
          <div className="form-group form-group-searchbar has-feedback has-feedback-left">
            <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
            <div className="input-group">
              <SearchFilter
                {...searchProps}
                placeholder="Search by typing a PO number, vendor name, invoice number or buyer..."
                submitOnSingleResult={r =>
                  pushHistory(`/purchase-orders/${r.purchase_order.uuid}`)}
              />
              <DecoratedExportButton
                searchProps={searchProps}
                currentUser={currentUser}
              />
              <DateRangePicker {...searchProps} />
              <FilterSelect
                {...searchProps}
                filters={filters.concat(...filtersWithLocations).concat({
                  label: CLEAR_FILTERS_TEXT,
                  filters: { ...defaultFilters }
                })}
                right
              />
            </div>
          </div>
          <TitleWithFilters
            {...searchProps}
            aggregateField="subtotal"
            title="Purchase Orders"
            filters={filterTags.concat(filterTagsWithLocations)}
            Sum={Sum}
          />
          <Table {...searchProps} />
          <Pagination {...searchProps} />
        </div>
      )}
    </Search>
  );
}

PurchaseOrdersSearch.propTypes = {
  pushHistory: PropTypes.func.isRequired
};

export default connect(undefined, { pushHistory: push })(PurchaseOrdersSearch);



// WEBPACK FOOTER //
// ./app/modules/PurchaseOrders/components/Search/Search.jsx